import { Tabs } from '@/components/tabs/Tabs';
import { useHoslogPlansList } from '@/features/configuration-management/hoslog-plans/hooks/useHoslogPlansList';
import { useHoslogPlansRequestList } from '@/features/configuration-management/hoslog-plans/planRequests/hooks/useHoslogPlanRequestList';
import { PlanRequestsTable } from '@/features/configuration-management/hoslog-plans/planRequests/table';
import { HoslogPlansTable } from '@/features/configuration-management/hoslog-plans/table';
import { encryptedStatusTypeMap } from '@/features/configuration-management/hoslog-plans/utils/hoslogPlan.constant';
import { useDynamicLocale } from '@/hoslog-intl';
import { createFileRoute, SearchSchemaInput, useSearch } from '@tanstack/react-router';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum([
    'planName',
    'planType',
    'planTime',
    'planStatus',
    'planCategory',
    'price',
    'createdDate',
    'sno',
    'updatedDate',
    'planTitle',
    'planPrice',
    'nomination',
    'status',
    'noOfPeople',
  ]),
  desc: z.boolean(),
});

const HoslogPlanSearchSchema = z
  .object({
    sort: z
      .preprocess((val) => {
        if (typeof val === 'string') {
          try {
            return JSON.parse(val);
          } catch {
            return undefined;
          }
        }
        return val;
      }, z.array(sortItemSchema))
      .optional(),

    createdDate: z.string().optional(),

    q: z
      .any()
      .optional()
      .transform((val) => (val == null ? undefined : String(val))),

    status: z
      .string()
      .transform((val) => val.split(','))
      .pipe(z.array(z.enum(['Active', 'Blocked'])))
      .optional(),
    statusFilter: z.enum(['A', 'R', 'P']).optional(),
    page: z.coerce.number().positive().catch(0),
    activeTab: z.enum(['L', 'R']).default('L'),
    fetch: z.coerce.number().positive().max(100).catch(10),
  })
  .passthrough()
  .transform((data) => {
    return {
      ...data,
      statusType: data.activeTab === 'R' ? encryptedStatusTypeMap.request : encryptedStatusTypeMap.list,
    };
  });

export const Route = createFileRoute('/(app)/configuration-management/hoslog-plans/')({
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => HoslogPlanSearchSchema.parse(search),
});

function RouteComponent() {
  const { activeTab } = useSearch({ from: '/(app)/configuration-management/hoslog-plans/' });

  const { hoslogPlansList, meta, isLoading } = useHoslogPlansList(activeTab, !!activeTab);
  const {
    hoslogPlansRequestList,
    requestMeta,
    isLoading: requestLoading,
  } = useHoslogPlansRequestList(activeTab, !!activeTab);

  const { t } = useDynamicLocale();

  const tabItems = [
    {
      label: t('configurationManagement.plans.title'),
      value: 'L',
      content: <HoslogPlansTable data={hoslogPlansList} isLoading={isLoading} pagination={meta} />,
    },
    {
      label: t('configurationManagement.plans.planRequest'),
      value: 'R',
      content: <PlanRequestsTable data={hoslogPlansRequestList} isLoading={requestLoading} pagination={requestMeta} />,
    },
  ];

  const [activeTabProp, setActiveTabProp] = useQueryState('activeTab', parseAsString.withDefault('list'));
  const [, setPage] = useQueryState('page', parseAsInteger.withDefault(0));
  const [, setCreatedDate] = useQueryState('createdDate', parseAsString);
  const [, setSearchQuery] = useQueryState('q', parseAsString);

  const onTabChange = (tab: string): void => {
    void Promise.all([setActiveTabProp(tab), setPage(0), setCreatedDate(null), setSearchQuery(null)]);
  };
  return (
    <Tabs
      className="rounded-lg shadow bg-muted"
      listClassName="bg-muted"
      triggerClassName="bg-muted rounded-t-[8px] px-6 py-3"
      defaultActive={activeTabProp}
      items={tabItems}
      onChange={onTabChange}
    />
  );
}
